import toast from "react-hot-toast";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { useNavigate } from "react-router";
import Toggle from "@/app/components/ui/Toggles";
import { useRouter } from "@/app/hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Input from "@/app/components/ui/Inputs/Input";
import { RegisterOptions, useForm } from "react-hook-form";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useConfigurationCityUpdateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityUpdateAPI";
import { useConfigurationCityDetailsAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityDetailsAPI";
import { useConfigurationCityOnboardAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityOnboardAPI";
import { useConfigurationCityDetailByPincodeAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityDetailByPincodeAPI";
import { convertFileToBase64, getCityPayload, handleDecimalInput, validateCityImage } from "@/app/hooks/custom_hooks";

export interface ICityOnboardFormFields {
  pincode: string;
  city: string;
  state: string;
  latitude: string;
  longitude: string;
  image: string;
  displayOnApp: boolean;
  displayOnVms: boolean;
}

const ConfigurationCityOnboard = () => {
  const detailsFetchedRef = useRef(false);
  const [isForEdit, setIsForEdit] = useState<boolean>(false);
  const { forOnlyNumber, forLatitude, forLongitude } = useFormValidations();
  const { getRouteKey } = useRouter();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { cityDetails, execute: fetchCityDetails } = useConfigurationCityDetailsAPI();
  const { updatedCityId, execute: updateCity } = useConfigurationCityUpdateAPI();
  const { cityByPincode: pincodeData, execute: fetchPincodeDetails } = useConfigurationCityDetailByPincodeAPI();
  const { newCityId, execute: onboardCity } = useConfigurationCityOnboardAPI();

  const formHook = useForm<ICityOnboardFormFields>({
    criteriaMode: "all",
    mode: "onChange",
    defaultValues: {
      image: "",
    },
  });

  const Submit = async (data: ICityOnboardFormFields) => {
    const isValid = await formHook.trigger();
    if (!data.city) {
      toast.error("Please enter a valid Pincode");
      return;
    }
    if (!isValid) return;
    const payload = getCityPayload(data, formHook.watch("image"));
    if (isForEdit) {
      updateCity({ city_id: cityDetails?.id, ...payload });
    } else {
      onboardCity(payload);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    clearError("image");
    const { isValid, error } = await validateCityImage(file);
    if (!isValid) {
      toast.error(error);
      return;
    }
    const base64 = await convertFileToBase64(file);
    formHook.setValue("image", base64);
  };

  const handleRemoveImage = () => {
    formHook?.setValue("image", "");
  };

  const clearError = (field: keyof ICityOnboardFormFields) => {
    formHook.clearErrors(field);
  };

  const handleCancel = () => {
    formHook?.reset();
    navigate(getRouteKey("CITY_SETUP", "url"));
  };

  useEffect(() => {
    if (newCityId) {
      navigate(getRouteKey("CITY_SETUP", "url"));
    }
  }, [newCityId]);

  useEffect(() => {
    if (updatedCityId) {
      navigate(getRouteKey("CITY_SETUP", "url"));
    }
  }, [updatedCityId]);

  useEffect(() => {
    const paramsCityId = params?.get("city_id");
    if (paramsCityId && !detailsFetchedRef.current) {
      fetchCityDetails(parseInt(paramsCityId));
      setIsForEdit(true);
      detailsFetchedRef.current = true;
    }
  }, [params?.get("city_id")]);

  useEffect(() => {
    if (cityDetails?.id) {
      formHook?.setValue("pincode", cityDetails?.pincode);
      formHook?.setValue("latitude", cityDetails?.lat);
      formHook?.setValue("longitude", cityDetails?.long);
      formHook?.setValue("city", cityDetails?.name);
      formHook?.setValue("state", cityDetails?.state);
      formHook?.setValue("image", cityDetails?.image_url);
      formHook?.setValue("displayOnApp", cityDetails?.display_on_app ? true : false);
    }
  }, [cityDetails?.id]);

  useEffect(() => {
    if (formHook.watch("pincode") == cityDetails?.pincode) {
      formHook?.setValue("city", cityDetails?.name);
      formHook?.setValue("state", cityDetails?.state);
    } else if (pincodeData) {
      formHook?.setValue("city", pincodeData?.District);
      formHook?.setValue("state", pincodeData?.State);
    }
  }, [pincodeData]);

  const pincode = formHook.watch("pincode");

  useEffect(() => {
    if (pincode?.length === 6) {
      fetchPincodeDetails(+pincode);
    }
  }, [pincode]);

  return (
    <div className="bg-white py-1 rounded-md">
      <div className="col-span-3">
        <div className="mx-3 mt-2 font-semibold text-base text-black">City Setup</div>
        <div className="mx-3">
          <form onSubmit={formHook?.handleSubmit(Submit)}>
            <div className="flex justify-start items-baseline mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">Pincode</span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              <Input
                register={formHook?.register("pincode", {
                  validate: (value) => {
                    const isNumeric = /^[0-9]*$/.test(value);
                    return isNumeric || "Only numbers are allowed";
                  },
                  required: true,
                  maxLength: 6,
                  minLength: 6,
                })}
                type="text"
                fieldError={formHook?.formState?.errors?.pincode}
                errorMessages={[
                  forOnlyNumber?.errors,
                  { type: "required", message: "Pincode is required" },
                  { type: "minLength", message: "Pincode should be of 6 digits" },
                  { type: "validate", message: "Only numbers are allowed" },
                ]}
                className="border-slate-400 rounded-md w-[300px] h-9 text-sm"
                maxLength={6}
                placeholder="Enter Pincode"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const inputVal = e.target?.value?.replace(/[^0-9]/g, "");
                  formHook?.setValue("pincode", inputVal);
                  formHook?.setValue("city", "");
                  formHook?.setValue("state", "");
                  formHook.trigger("pincode");
                }}
              />
            </div>
            <div className="flex justify-start items-baseline mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">City Name</span>
              </div>
              <Input
                register={formHook?.register("city", {
                  required: false,
                })}
                type="text"
                readOnly={true}
                errorMessages={[]}
                className="border-slate-400 rounded-md w-[300px] h-9 text-sm"
                fieldError={formHook?.formState?.errors?.city}
                placeholder="City Name"
              />
            </div>
            <div className="flex justify-start items-baseline mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">State Name</span>
              </div>
              <Input
                register={formHook?.register("state", {
                  required: false,
                })}
                type="text"
                readOnly={true}
                errorMessages={[]}
                className="border-slate-400 rounded-md w-[300px] h-9 text-sm"
                fieldError={formHook?.formState?.errors?.state}
                placeholder="State Name"
              />
            </div>
            <div className="flex justify-start items-baseline mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">Latitude</span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              <Input
                register={formHook?.register("latitude", {
                  ...((forLatitude?.validations as RegisterOptions<ICityOnboardFormFields, "latitude">) || {}),
                  required: "Latitude is required",
                })}
                type="text"
                className="border-slate-400 rounded-md w-[300px] h-9 text-sm"
                fieldError={formHook?.formState?.errors?.latitude}
                errorMessages={[forLatitude?.errors, { type: "required", message: "Latitude is required" }]}
                placeholder="Enter Latitude"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = handleDecimalInput(e.target.value);
                  formHook.setValue("latitude", val);
                  clearError("latitude");
                }}
              />
            </div>
            <div className="flex justify-start items-baseline mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">Longitude</span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              <Input
                register={formHook?.register("longitude", {
                  ...((forLongitude?.validations as RegisterOptions<ICityOnboardFormFields, "longitude">) || {}),
                  required: "Longitude is required",
                })}
                errorMessages={[forLongitude?.errors, { type: "required", message: "Longitude is required" }]}
                className="border-slate-400 rounded-md w-[300px] h-9 text-sm"
                fieldError={formHook?.formState?.errors?.longitude}
                placeholder="Enter Longitude"
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = handleDecimalInput(e.target.value);
                  formHook.setValue("longitude", val);
                  clearError("longitude");
                }}
              />
            </div>
            <div className="flex justify-start items-center mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">Image </span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              {formHook?.watch("image") ? (
                <div className="relative mt-3">
                  <img src={formHook?.watch("image")} alt="Selected Image" className="w-32 h-32" />
                  <div
                    className="top-0 absolute bg-transparent border-none text-black text-para-fontsize cursor-pointer"
                    onClick={handleRemoveImage}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                      <path
                        d="M14.25 5.3075L13.1925 4.25L9 8.4425L4.8075 4.25L3.75 5.3075L7.9425 9.5L3.75 13.6925L4.8075 14.75L9 10.5575L13.1925 14.75L14.25 13.6925L10.0575 9.5L14.25 5.3075Z"
                        fill="black"
                      />
                    </svg>
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    className={`px-4 py-2 rounded-md border border-slate-400 border-opacity-20 ${
                      formHook.formState.errors?.image && "!border-red-500"
                    }`}
                  >
                    <div className="relative w-[252px] h-[98px]">
                      <div
                        className={`top-[24px] absolute text-center text-slate-400 text-xs font-normal ${
                          formHook.formState.errors?.image && "!text-red-500"
                        }`}
                      >
                        Image should be .png format, 50 Px X 50 Px and 8 KB
                      </div>
                      <input
                        {...formHook?.register("image", {
                          required: true,
                        })}
                        type="file"
                        id="imageUpload"
                        accept=".png"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <div className="top-[72px] absolute w-[250px] text-center">
                        <label htmlFor="imageUpload" className="font-medium text-blue-600 text-xs cursor-pointer">
                          Upload
                        </label>
                      </div>
                    </div>
                  </div>
                  {formHook.formState.errors?.image && (
                    <div className="mt-1 text-[9px] text-red-500">Image is required</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-start items-center mt-5">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">Display on App</span>
              </div>
              <Toggle
                checked={formHook?.watch("displayOnApp") || false}
                onToggle={(state) => {
                  // console.log(state);
                  formHook.setValue("displayOnApp", state);
                }}
              />
            </div>
            <div className="flex justify-start items-center mt-5 mb-[8rem]">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">Display on VMS</span>
              </div>
              <Toggle
                checked={formHook?.watch("displayOnVms") || false}
                onToggle={(state) => {
                  console.log(state);
                  formHook.setValue("displayOnVms", state);
                }}
              />
            </div>
            <div className="fixed right-0 bottom-0 left-0 flex justify-end items-center gap-4 bg-white p-3 w-full border-t-2">
              <button type="button" onClick={handleCancel} className={SECONDARY_BUTTON}>
                Cancel
              </button>
              <button type="submit" className={PRIMARY_BUTTON}>
                {isForEdit ? "Update City" : "Onboard City"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationCityOnboard;
